import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exitPropertyWalkThroughForm,
  fetchConfigureTypeWalkThroughData,
} from 'src/store/actions/propertyWalkThroughForm';
import {
  setEasyWalkThroughFormData,
  exitEasyWalkThroughForm,
  fetchWalkThroughServiceList,
  downloadPDF,
} from 'src/store/actions/easyWalkThroughForm';
import { selectEasyWalkThroughFormData } from 'src/store/selectors/easyWalkThroughForm';
import Form from '../Form';
import Loader from '../Loader';
import FontAwesomeIcon from '../Icon';
import ReviewTable from '../ReviewTable/ReviewTable';
import Textarea from '../Textarea';
import { selectPropertyWalkThroughFormData } from 'src/store/selectors/propertyWalkThroughForm';
import Avatar from '../Avatar';
import { stopFormLoading } from 'src/store/actions/loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { SuccessMessages } from 'src/common/constants';
import { downloadDocument } from 'src/helpers/DownloadDoc';
import { ToastInfo } from 'src/helpers/validationHelper';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import './review-developer-walkthrough.scss';

type WalkthroughServiceRequestRow = Record<string, any>;

const ReviewDeveloperWalkthrough = () => {
  const dispatch = useDispatch();

  const propertyWalkThroughFormData = useSelector((state: any) =>
    selectPropertyWalkThroughFormData(state)
  );

  const easyWalkThroughFormData = useSelector((state: any) =>
    selectEasyWalkThroughFormData(state)
  );

  const pdfLoader = useSelector((state: any) => selectFormLoader(state));

  const [showImages, setShowImages] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const initializeWalkthroughReview = () => {
    dispatch(setEasyWalkThroughFormData({ walkThroughServiceRequests: [] }));
    dispatch(downloadPDF(easyWalkThroughFormData.walkThroughId));
    dispatch(fetchConfigureTypeWalkThroughData(easyWalkThroughFormData.walkThroughId));
    dispatch(fetchWalkThroughServiceList(easyWalkThroughFormData.walkThroughId));
    dispatch(stopFormLoading());
  };

  useEffect(() => {
    initializeWalkthroughReview();
  }, []);

  const handleShowImages = (data: WalkthroughServiceRequestRow) => {
    if (data && data.attachment) {
      const selectedImage = JSON.parse(data.attachment);

      setSelectedImages(selectedImage);
      setShowImages(true);
      setSelectedIndex(0);
    }
  };

  const displayImages = () => {
    return (
      <ReusableLightbox
        isOpen={showImages}
        onClose={() => setShowImages(false)}
        images={selectedImages}
        initialIndex={selectedIndex}
        onIndexChange={(index: number) => setSelectedIndex(index)}
      />
    );
  };

  const checkAttachment = (data: WalkthroughServiceRequestRow) => {
    if (data && data.attachment) {
      const attachment = data.attachment.split('/');
      return attachment.length > 0;
    }
  };

  const exportPDF = (pdfurl: string) => {
    ToastInfo(SuccessMessages.pdfDownloadNotification);
    downloadDocument(pdfurl);
  };

  const serviceRequests: WalkthroughServiceRequestRow[] =
    easyWalkThroughFormData.walkThroughServiceRequests &&
    easyWalkThroughFormData.walkThroughServiceRequests.length > 0
      ? easyWalkThroughFormData.walkThroughServiceRequests
      : [];

  const loading =
    easyWalkThroughFormData.reviewWalkThroughServiceRequestListLoading &&
    easyWalkThroughFormData.reviewWalkThroughServiceRequestListLoading ===
    true;

  return (
    <>
      <Form
        headline={'Walk Through Review & Confirmation'}
        strapline={''}
        onClose={() => {
          dispatch(exitEasyWalkThroughForm());
          dispatch(exitPropertyWalkThroughForm());
        }}
        component={
          loading ? (
            <div className="review-developer-walkthrough">
              <Loader center={true} />
            </div>
          ) : (
            <div className="review-developer-walkthrough">
              {pdfLoader && <Loader center={true} />}
              {easyWalkThroughFormData && easyWalkThroughFormData.PDFURL && (
                <div className="pdf-export-link">
                  <FontAwesomeIcon glyph="download" weight="light" />
                  <a
                    target="blank"
                    onClick={() => exportPDF(easyWalkThroughFormData.PDFURL)}
                  >
                    {' '}
                    Export to PDF
                  </a>
                </div>
              )}
              <div className="service-request-list">
                {serviceRequests && serviceRequests.length > 0 ? (
                  <ReviewTable
                    columns={[
                      {
                        xsHide: true,
                        label: 'Location',
                        cell: (data) =>
                          data.area && data.area.name
                            ? data.area.name
                            : data.areaName
                              ? data.areaName
                              : '',
                      },
                      {
                        xsHide: true,
                        label: 'Item',
                        cell: (data) => {
                          return data && data.itemName
                            ? data.itemName
                            : data &&
                                data.asset &&
                                data.asset.item &&
                                data.asset.item.name
                              ? data.asset.item.name
                              : '';
                        },
                      },
                      {
                        xsHide: true,
                        label: 'Issue Type',
                        cell: (data) =>
                          data.serviceType
                            ? data.serviceType
                            : data.issueType && data.issueType.name
                              ? data.issueType.name
                              : data.issueType
                                ? data.issueType
                                : '',
                      },
                      {
                        label: 'Description',
                        cell: (data) => data.description,
                      },
                      {
                        label: (
                          <FontAwesomeIcon glyph="paperclip" weight="light" />
                        ),
                        cell: (data) => (
                          <>
                            {checkAttachment(data) ? (
                              <div onClick={() => handleShowImages(data)}>
                                <Avatar
                                  badge={
                                    JSON.parse(data.attachment).length > 1
                                      ? 'count'
                                      : ''
                                  }
                                  url={JSON.parse(data.attachment)[0]}
                                />
                              </div>
                            ) : null}
                          </>
                        ),
                      },
                    ]}
                    data={serviceRequests}
                  />
                ) : null}
              </div>
              <div className="walkthrough-notes">
                <Textarea
                  value={
                    propertyWalkThroughFormData.configTypeSaved &&
                    propertyWalkThroughFormData.configTypeSaved.notes
                      ? propertyWalkThroughFormData.configTypeSaved.notes
                      : easyWalkThroughFormData && easyWalkThroughFormData.notes
                        ? easyWalkThroughFormData.notes
                        : ''
                  }
                  label="Notes"
                  placeholder="Notes"
                  disabled={true}
                  block
                />
              </div>

              {showImages === true ? <>{displayImages()}</> : null}
            </div>
          )
        }
        actions={undefined}
        footer={undefined}
        onSubmit={undefined}
        onExit={undefined}
      />
    </>
  );
};

export default ReviewDeveloperWalkthrough;